import { useRef } from 'react';
import { type FilterChip } from '@pkgs/components/utils/aiSearchChipUtils';
import { useDraftFilterChips, useFilterChips } from '@pkgs/components/hooks/useFilterChips';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { AiStarGradient } from '@pkgs/components/assets/icons/small/AiStarGradient';
import { InfoIconButton } from '@ev-tech/ui-core-components/Tooltip';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { type ModalState, useModalState } from '@ev-tech/ui-core-components/Modal';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { PreferencesDetailsModal } from '../PreferenceScores/PreferencesDetailsModal';
import { Chip } from '../Chip';

import styles from './AiExtractedFilters.module.css';

type AiExtractedFiltersProps = {
  preferencesInfoModalState?: ModalState;
  onChipDelete?: (chip: FilterChip) => void;
};

type AiExtractedFiltersContentProps = {
  filterChips: FilterChip[];
  preferenceChips: FilterChip[];
  preferencesInfoModalState?: ModalState;
  onChipDelete?: (chip: FilterChip) => void;
};

const AiExtractedFiltersContent = ({
  filterChips,
  preferenceChips,
  preferencesInfoModalState: externalPreferencesInfoModalState,
  onChipDelete,
}: AiExtractedFiltersContentProps) => {
  const { t } = useAppContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const internalpreferencesInfoModalState = useModalState();
  const preferencesInfoModalState = externalPreferencesInfoModalState ?? internalpreferencesInfoModalState;
  const isPreferencesUnavailable = useSearchStore((state) => state.isPreferencesUnavailable);
  return (
    <div className={styles.container}>
      <div className={styles.filtersSection}>
        <Overline text={t('search.aiSearch.filtersDropdown.filtersApplied')} />
        <div className={styles.filtersList}>
          {filterChips.map((filter) => (
            <Chip
              className={styles.filterChip}
              key={filter.id}
              label={filter.label}
              onDelete={onChipDelete && filter.isRemovable ? () => onChipDelete(filter) : undefined}
              deleteButtonLabel={`Remove ${filter.label}`}
              variant="default"
            />
          ))}
        </div>
      </div>
      <div className={styles.filtersSection}>
        <div className={styles.headingSection}>
          <Overline text={t('search.aiSearch.filtersDropdown.preferencesApplied')} />
          <InfoIconButton
            onClick={(e) => {
              e.stopPropagation();
              preferencesInfoModalState.open();
            }}
            label={t('search.aiSearch.scoresDropdown.infobutton.title')}
            ref={buttonRef}
          />
        </div>
        {preferenceChips.length > 0 && !isPreferencesUnavailable ? (
          <div className={styles.filtersList}>
            {preferenceChips.map((preference) => (
              <Chip
                className={styles.preferenceChip}
                key={preference.id}
                label={preference.label}
                icon={<AiStarGradient />}
                onDelete={onChipDelete ? () => onChipDelete(preference) : undefined}
                deleteButtonLabel={`Remove ${preference.label}`}
                variant="ai"
              />
            ))}
          </div>
        ) : (
          !isPreferencesUnavailable && <Copy text={t('search.aiSearch.filtersDropdown.noResults')} size="m" />
        )}
        {Boolean(isPreferencesUnavailable) && <Copy text={t('search.aiSearch.filtersDropdown.preferencesUnavailable')} size="m" />}
      </div>
      {!externalPreferencesInfoModalState && <PreferencesDetailsModal modalState={preferencesInfoModalState} />}
    </div>
  );
};

export const AiExtractedFilters = (props: AiExtractedFiltersProps) => {
  const { filterChips, preferenceChips } = useFilterChips();

  return <AiExtractedFiltersContent {...props} filterChips={filterChips} preferenceChips={preferenceChips} />;
};

export const AiDraftExtractedFilters = (props: AiExtractedFiltersProps) => {
  const { filterChips, preferenceChips } = useDraftFilterChips();

  return <AiExtractedFiltersContent {...props} filterChips={filterChips} preferenceChips={preferenceChips} />;
};
